/**
 * Contexto de Autenticación para Comanda App
 *
 * Maneja el estado de autenticación de usuarios COMANDA
 */

import React, { createContext, useState, useEffect, useContext } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn, signOut, confirmSignIn, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import awsConfig from '../aws-config';

// Configurar Amplify
Amplify.configure(awsConfig);

// Almacenar tokens en cookies con atributos Secure y SameSite en lugar de localStorage.
// Nota: no son httpOnly (Amplify necesita leer los tokens desde JS), pero eliminan
// el vector localStorage y agregan protección CSRF via SameSite.
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: window.location.hostname,
    secure: window.location.protocol === 'https:',
    path: '/resto/comanda/',
    sameSite: 'strict',
    expires: 30,
  })
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingPasswordChallenge, setPendingPasswordChallenge] = useState(null);

  // Verificar si hay un usuario autenticado al cargar la app
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      setLoading(true);
      console.log('[Comanda AuthContext] Verificando usuario autenticado...');
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      console.log('[Comanda AuthContext] Usuario autenticado:', {
        username: currentUser.username,
        userId: currentUser.userId
      });

      setUser({
        username: currentUser.username,
        userId: currentUser.userId,
        attributes: {
          email: currentUser.signInDetails?.loginId
        },
        tokens: {
          idToken: session.tokens?.idToken?.toString(),
          accessToken: session.tokens?.accessToken?.toString(),
          refreshToken: session.tokens?.refreshToken?.toString()
        }
      });
    } catch (err) {
      console.log('No hay usuario autenticado:', err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const result = await signIn({ username: email, password });

      if (result.isSignedIn) {
        await checkUser();
        return { success: true };
      } else if (result.nextStep) {
        const { signInStep } = result.nextStep;

        // Caso especial: Usuario necesita cambiar contraseña
        if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setPendingPasswordChallenge({ username: email });
          return {
            success: false,
            requiresNewPassword: true,
            message: 'Debes cambiar tu contraseña temporal'
          };
        }

        return {
          success: false,
          nextStep: signInStep,
          message: 'Acción adicional requerida'
        };
      }
    } catch (err) {
      console.error('Error en login:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    }
  };

  const logout = async () => {
    try {
      await signOut();
      setUser(null);
      return { success: true };
    } catch (err) {
      console.error('Error en logout:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    }
  };

  const confirmNewPassword = async (newPassword, name) => {
    try {
      setError(null);

      if (!pendingPasswordChallenge) {
        throw new Error('No hay un desafío de contraseña pendiente');
      }

      console.log('[Comanda AuthContext] Confirmando nueva contraseña...');

      // Confirmar el cambio de contraseña con Amplify
      // Incluir atributos requeridos por Cognito
      const result = await confirmSignIn({
        challengeResponse: newPassword,
        options: {
          userAttributes: {
            name: name
          }
        }
      });

      if (result.isSignedIn) {
        console.log('[Comanda AuthContext] Contraseña cambiada exitosamente, usuario autenticado');
        setPendingPasswordChallenge(null);

        // Esperar un momento para que Cognito procese el token
        await new Promise(resolve => setTimeout(resolve, 1000));

        await checkUser();
        return { success: true };
      } else {
        console.warn('[Comanda AuthContext] Contraseña cambiada pero requiere más pasos:', result.nextStep);
        return {
          success: false,
          nextStep: result.nextStep?.signInStep,
          message: 'Se requieren pasos adicionales'
        };
      }
    } catch (err) {
      console.error('Error confirmando nueva contraseña:', err);
      setError(err.message);
      return {
        success: false,
        error: err.message
      };
    }
  };

  const getAccessToken = async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.accessToken?.toString();
    } catch (err) {
      console.error('Error obteniendo token:', err);
      return null;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    confirmNewPassword,
    checkUser,
    getAccessToken,
    pendingPasswordChallenge,
    authLoading: loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
